import axios from 'axios';

import { FILESTORE_API_URL } from '../lib/environment.js';

import { newAbortSignalFunc } from './modules/api.js';
import { apiUrlToFindEntriesInFolder } from './modules/apiFunctions.js';

let entriesCancelToken = axios.CancelToken.source();
const timeoutAmount = 180000; //Aborts request after 30 seconds

const entries = () => {
	return {
		// MARK: Read
		/**
		 *
		 * @param {object} queryParams
		 * @param {number|string} limit 10
		 * @param {number|string} offset 48 (usually divisible 12 for the grid)
		 * @returns object
		 */
		read: async (rootPath, queryParams, limit, offset, sort, entryType) => {
			return new Promise((resolve, reject) => {
				axios({
					url: apiUrlToFindEntriesInFolder({
						rootPath,
						folderPath: queryParams?.folderPath,
						limit,
						offset,
						search: queryParams?.search,
						sort,
						entryType,
						queryString: queryParams?.queryString,
						showHidden: queryParams?.showHidden,
					}),
					method: 'GET',
					cache: 'no-cache',
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		// MARK: Read Folders
		/**
		 *
		 * @param {string} folderId 123abc
		 * @param {string} traverseDirection prev (up)| curr (current)| next (down) - DEPRECATED, use relType
		 * @param {string} partition The partition name (e.g., 'brand-assets', 'brand-library')
		 * @returns object
		 */
		readEntries: async (folderId, traverseDirection, partition = null) => {
			// FILESTORE_MIGRATION: Map old generation values to new relType values (backward compatible)
			const relTypeMap = {
				prev: 'parentAndSibs',
				curr: 'selfAndSibs',
				next: 'children',
			};
			const relType = relTypeMap[traverseDirection] || traverseDirection;

			return new Promise((resolve, reject) => {
				// FILESTORE_MIGRATION: Use partition-based route if partition is provided
				// Otherwise fall back to old route for backward compatibility
				const url = partition
					? `${FILESTORE_API_URL}/partitions/${partition}/entries/:key/relEntries$Folder?:key=${folderId}&relType=${relType}`
					: `${FILESTORE_API_URL}/entries/:key/relEntries.folder?:key=${folderId}&generation=${traverseDirection}`;

				axios({
					url,
					method: 'GET',
					cache: 'no-cache',
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		//MARK: Update
		/**
		 *
		 * @param {array} itemIdArray [asd123, 123abc, 321cba]
		 * @param {object} entryObject {isHidden: true, presentOnHomepage: false, ...}
		 * @param {string} partition The partition name (e.g., 'brand-assets', 'brand-library') - optional for backward compatibility
		 * @returns object|array
		 */
		// NOTE: 'isHidden' is inherited from the parent entry (implicit),
		// but the entry itself can also be 'isHidden' (persisted)
		//
		// WARNING: DO NOT save the 'isHidden' value from the parent entry
		//
		update: (itemIdArray, entryObject, partition = null) => {
			return new Promise((resolve, reject) => {
				// FILESTORE_MIGRATION: Use partition-based route if partition is provided
				const url = partition
					? `${FILESTORE_API_URL}/partitions/${partition}/entries/:itemIdArray`
					: `${FILESTORE_API_URL}/entries/:itemIdArray`;

				axios({
					url,
					method: 'PATCH',
					cache: 'no-cache',
					data: {
						':itemIdArray': itemIdArray,
						...entryObject,
					},
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		//MARK: Create Folder Path
		/**
		 *
		 * @param {string} partition The partition name (e.g., 'brand-assets', 'brand-library')
		 * @param {string} pathName /some/path (without partition prefix)
		 * @param {boolean} isHidden true or false
		 * @param {boolean} presentOnHomepage true or false
		 * @returns object
		 */
		createFolderPath: (
			partition,
			pathName,
			isHidden,
			presentOnHomepage = false,
		) => {
			return new Promise((resolve, reject) => {
				// FILESTORE_MIGRATION: Use partition-based route with $Folder notation
				// Path should NOT include partition prefix
				axios({
					url: `${FILESTORE_API_URL}/partitions/${partition}/entries$Folder?onConflict=rename`,
					method: 'POST',
					cache: 'no-cache',
					data: { path: pathName, isHidden, presentOnHomepage },
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		// MARK: - Update Path / Rename
		// This is for the Move or Rename functionality
		/**
		 *
		 * @param {array} idArray [abc123, 123abc, 321cba]
		 * @param {string} partition The partition name (e.g., 'brand-assets', 'brand-library')
		 * @param {string} pathName /some/path or /some/path/filename.ext
		 * @returns object
		 */
		updatePath: (itemId, partition, dstPath) => {
			return new Promise((resolve, reject) => {
				// Check if the path looks like a file (has an extension like .jpg, .png, .mp4, etc.)
				// If so, do NOT append trailing slash to indicate it's a file path, not a folder path
				const hasFileExtension = /\.[a-zA-Z0-9]{2,4}$/.test(dstPath);
				const formattedDstPath =
					dstPath && !hasFileExtension ? `${dstPath}/` : dstPath;

				//key/ path
				axios({
					url: `${FILESTORE_API_URL}/partitions/${partition}/jobs.entry.relocation?onConflict=rename`,
					method: 'POST',
					cache: 'no-cache',
					data: {
						src: {
							keys: itemId,
						},
						dstPath: formattedDstPath,
						runInForeground: true,
					},
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		//MARK: Delete
		/**
		 *
		 * @param {array} idArray [abc123, 123abc, 321cba]
		 * @param {string} partition The partition name (e.g., 'brand-assets', 'brand-library') - optional for backward compatibility
		 * @returns object
		 */
		delete: (idArray, partition) => {
			return new Promise((resolve, reject) => {
				axios({
					url: `${FILESTORE_API_URL}/partitions/${partition}/jobs.entry.deletion`,
					method: 'POST',
					cache: 'no-cache',
					data: {
						src: {
							keys: idArray, //NOTE: can be a single id or an array of id's
						},
						runInForeground: true, //idArray.length <= 10 // this number is arbitrary
					},
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		// MARK: Move / Copy
		/**
		 *
		 * @param {idArray} itemId [asd123, acb123]
		 * @param {string} partition The partition name (e.g., 'brand-assets', 'brand-library')
		 * @param {string} pathName /some/path
		 * @param {boolean} isCopy true or false
		 * @returns object
		 */
		move: (idArray, partition, dstPath, isCopy = false) => {
			// Note can "move" into an non-existing folder and it will create a new folder
			return new Promise((resolve, reject) => {
				axios({
					url: `${FILESTORE_API_URL}/partitions/${partition}/jobs.entry.${isCopy ? 'duplication' : 'relocation'}?onConflict=rename`,
					method: 'POST',
					cache: 'no-cache',
					data: {
						src: {
							keys: idArray, //NOTE: can be a single id or an array of id's
						},
						dstPath: dstPath !== '' ? `${dstPath}/` : dstPath,
						runInForeground: true, // idArray.length <= 10 // this number is arbitrary
					},
					withCredentials: true,
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		// MARK: Create Thumbnail
		/**
		 * @param {object} file
		 * @param {string} fileName
		 * @param {boolean} isCustomThumbnail
		 * @returns object
		 */
		createThumbnail: (file, fileName, isCustomThumbnail = false) => {
			// FILESTORE_MIGRATION: Use partition-based route
			const path = isCustomThumbnail
				? `custom/${fileName}`
				: `default/${fileName}`;

			return new Promise((resolve, reject) => {
				axios({
					url: `${FILESTORE_API_URL}/partitions/brand-thumbnails/entries$File?onConflict=rename&path=${encodeURIComponent(path)}`,
					method: 'POST',
					cache: 'no-cache',
					data: file,
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},
		// MARK: Delete Thumbnail
		/**
		 * @param {string} fileName
		 * @returns object
		 */
		deleteThumbnail: async (fileName) => {
			return new Promise((resolve, reject) => {
				// FILESTORE_MIGRATION: Use partition-based route
				const path = `custom/${fileName}`;

				axios({
					url: `${FILESTORE_API_URL}/partitions/brand-thumbnails/entries/:fileURL`,
					method: 'DELETE',
					cache: 'no-cache',
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
					data: {
						':fileURL': path,
					},
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},
		// MARK: Upload Temporary File
		/**
		 * @param {File} file
		 * @returns {Promise<object>}
		 */
		uploadTemporaryFile: async (file) => {
			return new Promise((resolve, reject) => {
				const formData = new FormData();
				formData.append('file', file);
				const safeBaseName = file.name.replace(/[/\\]/g, '_');
				const fileName = `${Date.now()}_${safeBaseName}`;

				// FILESTORE_MIGRATION: Use partition-based route
				// Path should be clean without partition prefix
				const tempPath = `polotno-temp/${fileName}`;

				axios({
					url: `${FILESTORE_API_URL}/partitions/brand-library/entries$File`,
					method: 'POST',
					data: formData,
					params: { onConflict: 'rename', path: tempPath },
					withCredentials: true,
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
					cancelToken: entriesCancelToken.token,
				})
					.then((resp) => {
						resolve({ ...resp.data, fileName });
					})
					.catch((err) => {
						reject(err);
					});
			});
		},

		// MARK: Cleanup Temporary Files
		/**
		 * @param {string} fileName
		 * @returns {Promise<object>}
		 */
		cleanupTemporaryFiles: async (fileName) => {
			return new Promise((resolve, reject) => {
				// FILESTORE_MIGRATION: Use partition-based route
				// Path should be clean without partition prefix
				const fileURL = `polotno-temp/${fileName}`;

				axios({
					url: `${FILESTORE_API_URL}/partitions/brand-library/entries/:fileURL`,
					method: 'DELETE',
					cache: 'no-cache',
					withCredentials: true,
					timeout: timeoutAmount,
					signal: newAbortSignalFunc(timeoutAmount),
					cancelToken: entriesCancelToken.token,
					data: {
						':fileURL': fileURL,
					},
				})
					.then((resp) => {
						resolve(resp.data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},
	};
};

const singleton = entries();
Object.freeze(singleton);

export default singleton;
